import 'react-datepicker/dist/react-datepicker.css';

import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBoolean,
  useOutsideClick,
  useMediaQuery,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import Calendar from 'components/Icons/Calendar';
import PlanPermission from 'container/PlanPermission';
import { TimeProperties } from 'models/response/model.interface';
import DatePickerInputButton from 'pages/ModelOverviewPage/DatePickerWithButton';
import TimeScalePermission from 'pages/PermissionComponent/TimeScalePermission';
import { getBlocks } from 'redux/ModelsSlice';
import { modelDetailStatus } from 'redux/PlanPageSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import { getDashboard, setLoading } from 'redux/DashboardSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getReportingPeriodDisplay } from 'utils/reportingPeriodUtils';
import {
  setTimeFilter,
  setDashboardReportingPeriod,
  getDashboardReportingPeriod,
  getIsPageBarOpen,
  getTimeFilter,
  getSelectedPage,
  getSelectedDashboardScenario,
  fetchAllDataValues,
} from 'redux/DashboardSliceV2';

export interface Properties {
  planPermission: boolean;
  rangeSelected: (start: string, end: string) => void;
}

const TimeRangeFilter = ({ planPermission, rangeSelected }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const [timescale, setTimescale] = useState('Y');
  const [maxValue, setMaxValue] = useState(3);
  const [periodAmount, setPeriodAmount] = useState('3');
  const [endIn, setEndIn] = useState<Date | any>(new Date());
  const [isPermissionTimescaleModal, setIsPermissionTimescaleModal] = useState(false);
  const [startIn, setStartIn] = useState<Date | any>(new Date());
  const model = useSelector(getBlocks);
  const modelStatus = useSelector(modelDetailStatus);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const presetReference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [timeScalePopover, setTimeScalePopover] = useBoolean();
  const [presetPopover, setPresetPopover] = useBoolean();
  const [filterStartDate, setFilterStartDate] = useState<Date | any>();
  const [filterEndDate, setFilterEndDate] = useState<Date | any>();
  const dashboardReportingPeriod = useSelector(getDashboardReportingPeriod);
  const [reportingPreset, setReportingPreset] = useState<string>(
    dashboardReportingPeriod?.preset || 'Custom',
  );
  const [customStartDate, setCustomStartDate] = useState<string>(
    dashboardReportingPeriod?.start || '',
  );
  const [customEndDate, setCustomEndDate] = useState<string>(dashboardReportingPeriod?.end || '');
  const [customStartDateObject, setCustomStartDateObject] = useState<Date | null>(null);
  const [customEndDateObject, setCustomEndDateObject] = useState<Date | null>(null);
  const formatTimeProperties = (properties: TimeProperties) => {
    return { ...properties, time_granularity: properties?.time_granularity ?? 'M' };
  };
  const properties = formatTimeProperties(model?.time_properties);
  const fyStartMonth = useMemo(() => {
    const month = model?.time_properties?.fy_start_month;
    return (month as string | undefined) ?? undefined;
  }, [model?.time_properties?.fy_start_month]);
  const timeGranualityLimit =
    UserSubscriptionLimitData?.subscription_features_limit?.model_timescale;
  const dashboard = useSelector(getDashboard);
  const timeFilter = useSelector(getTimeFilter);
  const [smallDevice] = useMediaQuery('(max-width: 1100px)');
  const isPageBarOpen = (useSelector(getIsPageBarOpen) && smallDevice) || smallDevice;
  const selectedPage = useSelector(getSelectedPage);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const isSmallDevice = useMediaQuery('(max-width: 1024px)') && isPageBarOpen;
  const formatFilterDate = () => {
    if (filterStartDate && filterEndDate) {
      const startDate = new Date(filterStartDate).toDateString();
      setFilterStartDate(
        timescale === 'Y' ? dayjs(startDate).format('YYYY') : dayjs(startDate).format('DD MMM YY'),
      );
      const endDate = new Date(filterEndDate).toDateString();
      setFilterEndDate(
        timescale === 'Y' ? dayjs(endDate).format('YYYY') : dayjs(endDate).format('DD MMM YY'),
      );
    }
  };

  const onRangeSelected = (start: string, end: string) => {
    rangeSelected(start, end);
    setTimeScalePopover.off();
  };

  useEffect(() => {
    if (timeGranualityLimit === null) {
      setMaxValue(-1);
    } else {
      if (timescale.toLowerCase() === 'm') {
        setMaxValue(timeGranualityLimit);
      }
      if (timescale.toLowerCase() === 'y') {
        setMaxValue(timeGranualityLimit / 12);
      }
    }
  }, [timescale, timeGranualityLimit]);

  useEffect(() => {
    formatFilterDate();
  }, [filterStartDate, filterEndDate]);
  useEffect(() => {
    if (properties) {
      const timeGranularity = properties?.time_granularity ?? 'M';
      setTimescale(timeGranularity.toUpperCase());
      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);
      setPeriodAmount(value.toString());
      setStartIn(properties?.starting_period ? new Date(properties?.starting_period) : null);
      setFilterStartDate(
        properties?.starting_period ? new Date(properties?.starting_period) : null,
      );
    }
    if (properties && properties?.starting_period) {
      const endDate = new Date(properties?.starting_period);
      const timeGranularity = properties?.time_granularity ?? 'M';

      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);
      const targetValue = timeGranularity.toLowerCase() === 'm' ? value : value * 12;
      const dateEnd = endDate.setMonth(endDate.getMonth() + targetValue);
      setEndIn(new Date(dateEnd));
      setFilterEndDate(new Date(dateEnd));
    } else {
      setEndIn(null);
    }
  }, [modelStatus]);

  const calculateShowData = (data: any) => {
    if (timescale === 'Y') {
      return data?.getFullYear();
    }
    return `${data?.toLocaleDateString('en-US', {
      month: 'short',
    })} ${data?.getFullYear().toString().slice(-2)}`;
  };

  const getExactDate = (dateString: string) => {
    if (properties?.time_granularity?.toLowerCase() === 'y') return dateString;
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const dd = new Date(dateString);
    const month = months[dd.getMonth()]; // Get the month name from the array
    const year = dd.getFullYear().toString().slice(-2); // Get the last two digits of the year
    const newString = `${month}-${year}`;
    return newString;
  };

  const convertToDate = (input: string) => {
    const months: any = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    // Validate the input format
    const match = input.match(/^([A-Za-z]{3})-(\d{2})$/);
    if (!match) return null;

    const [, month, yearSuffix] = match;
    const monthIndex = months[month];
    if (monthIndex === undefined) return null;

    // Convert the year suffix to a full year
    const currentYear = new Date().getFullYear();
    const century = Math.floor(currentYear / 100) * 100;
    const year = century + Number.parseInt(yearSuffix, 10);

    // Create the date object and format it
    const date = new Date(year, monthIndex, 1);
    return date;
  };

  const isValidMonthYearFormat = (input: string): boolean => {
    // Regular expression to check "MMM-YY" format (e.g., "Jan-26", "Feb-24")
    const monthYearRegex = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}$/;

    // Check if input matches the pattern
    if (!monthYearRegex.test(input)) return false;

    // Extract year and convert to a full four-digit year
    const [, month, shortYear] =
      input.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{2})/) || [];
    if (!month || !shortYear) return false;

    // Convert short year to full year (assume 20XX for simplicity)
    const fullYear = Number.parseInt(`20${shortYear}`, 10);

    // Check if the parsed date is valid
    return moment(`${month} ${fullYear}`, 'MMM YYYY', true).isValid();
  };

  useEffect(() => {
    if (properties && dashboard?.id && modelId) {
      dispatch(setLoading(true));
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      const object = dashboard?.id && modalFilters.timeFilter ? modalFilters.timeFilter || {} : {};
      if (
        object &&
        dashboard?.id &&
        isValidMonthYearFormat(object?.startDatePlanPage) &&
        isValidMonthYearFormat(object?.endDatePlanPage)
      ) {
        const { startDatePlanPage, endDatePlanPage } = object;
        setStartIn(convertToDate(startDatePlanPage));
        setEndIn(convertToDate(endDatePlanPage));
        onRangeSelected(startDatePlanPage, endDatePlanPage);
        dispatch(setTimeFilter({ start: startDatePlanPage, end: endDatePlanPage }));
      } else {
        const timeGranularity = properties?.time_granularity ?? 'M';
        setTimescale(timeGranularity.toUpperCase());
        const value = Number.isNaN(properties?.plan_duration_periods)
          ? timeGranularity.toLowerCase() === 'm'
            ? 36
            : 3
          : Number(properties?.plan_duration_periods);
        setPeriodAmount(value.toString());
        setStartIn(properties?.starting_period ? new Date(properties?.starting_period) : null);
        setFilterStartDate(
          properties?.starting_period ? new Date(properties?.starting_period) : null,
        );

        const endDate = new Date(properties?.starting_period);

        const targetValue = timeGranularity.toLowerCase() === 'm' ? value : value * 12;
        const dateEnd = endDate.setMonth(endDate.getMonth() + targetValue);
        setEndIn(new Date(dateEnd));
        setFilterEndDate(new Date(dateEnd));
        onRangeSelected(
          getExactDate(dayjs(new Date(properties.starting_period)).format('DD MMM YY')),
          getExactDate(dayjs(new Date(dateEnd)).format('DD MMM YY')),
        );
        dispatch(
          setTimeFilter({
            start: getExactDate(new Date(properties.starting_period).toLocaleString()),
            end: getExactDate(new Date(dateEnd).toLocaleString()),
          }),
        );
      }
      dispatch(setLoading(false));
    }
  }, [dashboard?.id, modelId]);

  const applyPlanPageFilter = (startDatePlanPage: string, endDatePlanPage: string) => {
    onRangeSelected(startDatePlanPage, endDatePlanPage);
    dispatch(setTimeFilter({ start: startDatePlanPage, end: endDatePlanPage }));

    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    if (modelId) {
      const modalFilters = data[modelId] || {};

      // Ensure timeFilter exists
      if (!modalFilters.timeFilter) {
        modalFilters.timeFilter = {};
      }

      // Set the time range for the dashboard id
      modalFilters.timeFilter = { startDatePlanPage, endDatePlanPage };

      // Save back to localStorage
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [modelId]: modalFilters }),
      );
    }
  };

  const changeTimeInput = useCallback(
    (time: string, amount: string, startDate: Date | null, endDate?: Date | null) => {
      const requestObject: any = {
        model_id: Number(modelId),
        request_body: {
          time_granularity: time,
          plan_duration_periods: amount,
        },
      };
      if (endDate) {
        const endInDate = endDate.toDateString();
        requestObject.request_body.ending_period =
          time === 'Y' ? dayjs(endInDate).format('YYYY') : dayjs(endInDate).format('DD MMM YY');
      }
      if (startDate !== null) {
        const startInDate = startDate.toDateString();
        requestObject.request_body.starting_period =
          time === 'Y' ? dayjs(startInDate).format('YYYY') : dayjs(startInDate).format('DD MMM YY');
      }
      const endDatePlanPage = getExactDate(requestObject.request_body.ending_period);
      const startDatePlanPage = getExactDate(requestObject.request_body.starting_period);
      applyPlanPageFilter(startDatePlanPage, endDatePlanPage);
    },
    [modelId, filterStartDate, filterEndDate],
  );

  const onChangePeriod = async (valueDiff: any) => {
    const newValue = valueDiff;
    if (Number(newValue) <= maxValue) {
      setPeriodAmount(newValue);
    } else if (maxValue === -1) {
      setPeriodAmount(newValue);
    } else {
      setIsPermissionTimescaleModal(true);
      setTimeout(() => {
        const timeGranularity = properties?.time_granularity ?? 'M';
        const value = Number.isNaN(properties?.plan_duration_periods)
          ? timeGranularity.toLowerCase() === 'm'
            ? 36
            : 3
          : Number(properties?.plan_duration_periods);
        setPeriodAmount(value.toString());
      }, 1000);
      setTimeout(() => {
        setIsPermissionTimescaleModal(false);
      }, 5000);
    }
  };

  const startDateChange = (startDate: Date) => {
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endIn.getFullYear() - startDate.getFullYear()) * 12 +
        (endIn.getMonth() - startDate.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endIn.getFullYear() - startDate.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const endDateChange = (endDate: Date) => {
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDate.getFullYear() - startIn.getFullYear()) * 12 +
        (endDate.getMonth() - startIn.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDate.getFullYear() - startIn.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const onSelectStartDate = async (date: Date) => {
    setStartIn(date);
    const diff = startDateChange(date);
    onChangePeriod(Number(diff));
    changeTimeInput(timescale, diff, date, endIn);
  };

  const onSelectEndDate = useCallback(
    (date: Date) => {
      setEndIn(date);
      const diff = endDateChange(date);
      onChangePeriod(Number(diff));
      changeTimeInput(timescale, diff, startIn, date);
    },
    [changeTimeInput, periodAmount, timescale],
  );

  const handleClearSelection = () => {
    setStartIn(null);
  };

  const handleCustomDateChange = (start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);

    // Save to Redux
    dispatch(
      setDashboardReportingPeriod({
        preset: 'Custom',
        start,
        end,
      }),
    );

    // Save to localStorage
    if (model?.id) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[model.id] || {};
      modalFilters.reportingPeriod = {
        preset: 'Custom',
        start,
        end,
      };
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [model.id]: modalFilters }),
      );
    }

    // Call data_values API after custom date selection (only if both dates are selected)
    if (
      start &&
      end &&
      selectedPage?.id &&
      selectedDashboardScenario?.id &&
      timeFilter?.start &&
      timeFilter?.end
    ) {
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage.id,
          scenarioId: selectedDashboardScenario.id,
        }),
      );
    }
  };

  const handleClearCustomStartDate = () => {
    setCustomStartDateObject(null);
    setCustomStartDate('');
    handleCustomDateChange('', customEndDate);
  };

  const handleClearCustomEndDate = () => {
    setCustomEndDateObject(null);
    setCustomEndDate('');
    handleCustomDateChange(customStartDate, '');
  };

  const handlePresetChange = (preset: string) => {
    setReportingPreset(preset);
    if (preset === 'Custom') {
      // Keep existing custom dates and keep popover open for date selection
      setPresetPopover.off();
      // Keep the main time scale popover open so users can select custom dates
      return;
    }

    // For non-custom presets, start and end should be empty strings
    const startValue = '';
    const endValue = '';

    // Save to Redux
    dispatch(
      setDashboardReportingPeriod({
        preset,
        start: startValue,
        end: endValue,
      }),
    );

    // Save to localStorage
    if (model?.id) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[model.id] || {};
      modalFilters.reportingPeriod = {
        preset,
        start: startValue,
        end: endValue,
      };
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [model.id]: modalFilters }),
      );
    }

    setPresetPopover.off();

    // Call data_values API after preset change
    if (selectedPage?.id && selectedDashboardScenario?.id && timeFilter?.start && timeFilter?.end) {
      dispatch(
        fetchAllDataValues({
          pageId: selectedPage.id,
          scenarioId: selectedDashboardScenario.id,
        }),
      );
    }
  };

  const handleCustomStartDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = getExactDate(dayjs(date).format('DD MMM YY'));
      setCustomStartDateObject(date);
      setCustomStartDate(formattedDate);
      handleCustomDateChange(formattedDate, customEndDate);
    } else {
      // Handle null/clear case
      setCustomStartDateObject(null);
      setCustomStartDate('');
      handleCustomDateChange('', customEndDate);
    }
  };

  const handleCustomEndDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = getExactDate(dayjs(date).format('DD MMM YY'));
      // Set both the date object and string immediately
      setCustomEndDateObject(date);
      setCustomEndDate(formattedDate);
      handleCustomDateChange(customStartDate, formattedDate);
    } else {
      // Handle null/clear case
      setCustomEndDateObject(null);
      setCustomEndDate('');
      handleCustomDateChange(customStartDate, '');
    }
  };

  // Update custom date objects when dates are loaded from Redux or localStorage
  // This ensures dates are properly synced when loaded from Redux on component mount
  useEffect(() => {
    if (reportingPreset === 'Custom') {
      // Handle start date
      if (customStartDate && customStartDate.trim()) {
        // Try to parse as month format first (e.g., "Jan-26")
        let parsedDateObject: Date | null = null;
        if (isValidMonthYearFormat(customStartDate)) {
          parsedDateObject = convertToDate(customStartDate);
        } else {
          // Fallback to old DD-MM-YYYY format for backward compatibility
          const parsedDate = dayjs(customStartDate, 'DD-MM-YYYY', true);
          if (parsedDate.isValid()) {
            parsedDateObject = parsedDate.toDate();
            // Convert to month format and update the stored value
            const monthFormat = getExactDate(dayjs(parsedDateObject).format('DD MMM YY'));
            setCustomStartDate(monthFormat);
          }
        }

        if (parsedDateObject) {
          const currentDateString = customStartDateObject
            ? getExactDate(dayjs(customStartDateObject).format('DD MMM YY'))
            : '';
          const parsedDateString = getExactDate(dayjs(parsedDateObject).format('DD MMM YY'));
          // Only update if the date string is different to avoid unnecessary updates
          if (parsedDateString !== currentDateString) {
            setCustomStartDateObject(parsedDateObject);
          }
        } else if (customStartDateObject !== null) {
          setCustomStartDateObject(null);
        }
      } else if (customStartDateObject !== null) {
        setCustomStartDateObject(null);
      }

      // Handle end date
      if (customEndDate && customEndDate.trim()) {
        // Try to parse as month format first (e.g., "Jan-26")
        let parsedDateObject: Date | null = null;
        if (isValidMonthYearFormat(customEndDate)) {
          parsedDateObject = convertToDate(customEndDate);
        } else {
          // Fallback to old DD-MM-YYYY format for backward compatibility
          const parsedDate = dayjs(customEndDate, 'DD-MM-YYYY', true);
          if (parsedDate.isValid()) {
            parsedDateObject = parsedDate.toDate();
            // Convert to month format and update the stored value
            const monthFormat = getExactDate(dayjs(parsedDateObject).format('DD MMM YY'));
            setCustomEndDate(monthFormat);
          }
        }

        if (parsedDateObject) {
          const currentDateString = customEndDateObject
            ? getExactDate(dayjs(customEndDateObject).format('DD MMM YY'))
            : '';
          const parsedDateString = getExactDate(dayjs(parsedDateObject).format('DD MMM YY'));
          // Only update if the date string is different to avoid unnecessary updates
          if (parsedDateString !== currentDateString) {
            setCustomEndDateObject(parsedDateObject);
          }
        } else if (customEndDateObject !== null) {
          setCustomEndDateObject(null);
        }
      } else if (customEndDateObject !== null) {
        setCustomEndDateObject(null);
      }
    }
  }, [customStartDate, customEndDate, reportingPreset]);

  // Load reporting period from localStorage on mount
  useEffect(() => {
    if (model?.id) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[model.id] || {};
      if (modalFilters?.reportingPeriod) {
        const { preset, start, end } = modalFilters.reportingPeriod;
        // Only load if Redux doesn't have a preset or if localStorage has different values
        if (
          !dashboardReportingPeriod?.preset ||
          dashboardReportingPeriod?.preset !== preset ||
          dashboardReportingPeriod?.start !== start ||
          dashboardReportingPeriod?.end !== end
        ) {
          dispatch(
            setDashboardReportingPeriod({
              preset: preset || 'Custom',
              start: start || '',
              end: end || '',
            }),
          );
        }
      }
    }
  }, [model?.id]);

  // Load reporting period from Redux (only sync when preset changes, not on every date change)
  useEffect(() => {
    if (dashboardReportingPeriod) {
      const { preset, start, end } = dashboardReportingPeriod;
      const newPreset = preset || 'Custom';

      // Only update preset if it's different
      if (newPreset !== reportingPreset) {
        setReportingPreset(newPreset);
        // When preset changes, sync dates from Redux
        if (newPreset === 'Custom') {
          if (start && end) {
            setCustomStartDate(start);
            setCustomEndDate(end);
          } else {
            setCustomStartDate('');
            setCustomEndDate('');
          }
        }
      }
    }
  }, [dashboardReportingPeriod?.preset]);

  // Initialize Custom Date Range with Display Timescale values when Custom is selected and dates are empty
  useEffect(() => {
    if (
      reportingPreset === 'Custom' &&
      startIn &&
      endIn && // Only initialize if custom dates are empty
      !customStartDate &&
      !customEndDate
    ) {
      const startDateFormatted = getExactDate(dayjs(startIn).format('DD MMM YY'));
      const endDateFormatted = getExactDate(dayjs(endIn).format('DD MMM YY'));
      setCustomStartDate(startDateFormatted);
      setCustomEndDate(endDateFormatted);
      setCustomStartDateObject(startIn);
      setCustomEndDateObject(endIn);
      // Save to Redux and localStorage
      dispatch(
        setDashboardReportingPeriod({
          preset: 'Custom',
          start: startDateFormatted,
          end: endDateFormatted,
        }),
      );
      // Save to localStorage
      if (model?.id) {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data = planFilters ? JSON.parse(planFilters) : {};
        const modalFilters = data[model.id] || {};
        modalFilters.reportingPeriod = {
          preset: 'Custom',
          start: startDateFormatted,
          end: endDateFormatted,
        };
        localStorage.setItem(
          'global-modal-filters',
          JSON.stringify({ ...data, [model.id]: modalFilters }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingPreset, startIn, endIn, customStartDate, customEndDate]);

  useOutsideClick({
    ref: reference,
    handler: () => {
      setTimeScalePopover.off();
    },
  });

  useOutsideClick({
    ref: presetReference,
    handler: () => {
      setPresetPopover.off();
    },
  });
  return (
    <>
      <Flex
        ps={0}
        gap={1}
        mr={2}
        borderLeftColor={'white'}
        backgroundColor={'#332663'}
        padding={'.5rem 1rem'}
        borderRightRadius={'sm'}
        height={'40px'}
        width={'max-content'}
      >
        <Popover
          placement='bottom-start'
          closeOnBlur={false}
          isOpen={timeScalePopover}
          onOpen={setTimeScalePopover.on}
          onClose={setTimeScalePopover.off}
          isLazy
          lazyBehavior='keepMounted'
        >
          <Box ref={reference}>
            <PopoverTrigger>
              <Flex
                gap={1}
                cursor={'pointer'}
                alignItems={'center'}
                title={`${calculateShowData(startIn)} - ${calculateShowData(endIn)}`}
              >
                <Calendar
                  style={{
                    marginRight: '.2rem',
                  }}
                  width={5}
                  height={6}
                  color='#69B4B8'
                />
                <ChevronDownIcon w={5} h={5} />
                {!isSmallDevice && (
                  <Text ml={1} size={'Roboto-14'}>
                    {`${calculateShowData(startIn)} - ${calculateShowData(endIn)}`}
                  </Text>
                )}
              </Flex>
            </PopoverTrigger>
            <PopoverContent
              filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
              bg='white'
              border='none'
              mt={3}
              borderRadius={'8px'}
            >
              <PopoverBody ps={4}>
                <Box pt={4} pb={2}>
                  <Text color='#6562CF' fontSize={'xs'} fontWeight={'600'}>
                    Reporting Period
                  </Text>
                </Box>
                <Popover
                  placement='bottom-start'
                  closeOnBlur={false}
                  isOpen={presetPopover}
                  onOpen={setPresetPopover.on}
                  onClose={setPresetPopover.off}
                  isLazy
                  lazyBehavior='keepMounted'
                >
                  <Box ref={presetReference}>
                    <PopoverTrigger>
                      <Flex
                        alignItems='center'
                        justifyContent='space-between'
                        cursor='pointer'
                        px={2}
                        py={2}
                        borderRadius='8px'
                        _hover={{ bg: '#F5F5F5' }}
                        mb={3}
                        border={'1px solid grey'}
                      >
                        <Text color='black' fontSize={'xs'}>
                          {getReportingPeriodDisplay(
                            dashboardReportingPeriod,
                            fyStartMonth,
                            reportingPreset,
                          )}
                        </Text>
                        <ChevronDownIcon boxSize={4} color='black' />
                      </Flex>
                    </PopoverTrigger>
                    <PopoverContent
                      ml={{ base: '5', md: '3' }}
                      filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
                      bg='white'
                      maxWidth='80vw'
                      borderRadius='14px'
                    >
                      <Box pt={4} pb={1} px={5}>
                        <Text color='#6562CF' fontSize={'xs'} fontWeight={'600'}>
                          Select Preset
                        </Text>
                      </Box>
                      <PopoverBody>
                        <Flex direction={'column'}>
                          {[
                            'Current Month',
                            'Last Month',
                            'Financial Year to Last Month',
                            'This Financial Year',
                            'Custom',
                          ].map((preset) => (
                            <Flex
                              key={preset}
                              alignItems={'center'}
                              justifyContent={'space-between'}
                              cursor='pointer'
                              borderRadius='14px'
                              px='1'
                              py='2'
                              onClick={(event) => {
                                event.stopPropagation();
                                handlePresetChange(preset);
                              }}
                              bg={reportingPreset === preset ? '#EFEEFE' : 'transparent'}
                            >
                              <Box
                                display={'flex'}
                                color={'#8B89B1'}
                                alignItems={'center'}
                                w={'100%'}
                              >
                                {reportingPreset === preset && <CheckIcon />}
                                <Box flex={1} px='2' py='0'>
                                  <Text color='black' fontSize={'xs'}>
                                    {preset}
                                  </Text>
                                  <Text color='#8B8AAB' fontSize={'10px'} mt={0.5}>
                                    {getReportingPeriodDisplay(
                                      dashboardReportingPeriod,
                                      fyStartMonth,
                                      preset,
                                    )
                                      .replace(preset, '')
                                      .trim()}
                                  </Text>
                                </Box>
                              </Box>
                            </Flex>
                          ))}
                        </Flex>
                      </PopoverBody>
                    </PopoverContent>
                  </Box>
                </Popover>
                {reportingPreset === 'Custom' && (
                  <>
                    <Box pb={2}>
                      <Text color='#6562CF' fontSize={'xs'} fontWeight={'600'}>
                        Custom Date Range
                      </Text>
                    </Box>
                    <Flex justify={'start'} align={'center'} pb={3}>
                      <DatePickerInputButton
                        clearSection={false}
                        clearDateInput={handleClearCustomStartDate}
                        selected={customStartDateObject}
                        onChange={handleCustomStartDateChange}
                        timescale={timescale}
                        isDisabled={planPermission}
                        minDate={new Date(filterStartDate)}
                        maxDate={customEndDateObject || new Date(endIn)}
                        placeholder='Start'
                      />
                      <Flex px={2}>
                        <BsArrowRight color={'black'} style={{ width: '20px', height: '20px' }} />
                      </Flex>
                      <DatePickerInputButton
                        clearSection={false}
                        clearDateInput={handleClearCustomEndDate}
                        selected={customEndDateObject}
                        onChange={handleCustomEndDateChange}
                        timescale={timescale}
                        isDisabled={planPermission}
                        minDate={customStartDateObject || new Date(startIn)}
                        maxDate={new Date(filterEndDate)}
                        placeholder='End'
                      />
                    </Flex>
                    {customStartDate && customEndDate && (
                      <Text color={'#8B8AAB'} fontSize={'12px'} ps={1}>
                        {customStartDate} to {customEndDate}
                      </Text>
                    )}
                  </>
                )}
                <Divider orientation='horizontal' mb={3} />
                <Box pb={2}>
                  <Text color='#6562CF' fontSize={'xs'} fontWeight={'600'}>
                    Display Timescale
                  </Text>
                </Box>
                <Flex justify={'start'} align={'center'} pb={3}>
                  <DatePickerInputButton
                    clearSection={false}
                    clearDateInput={handleClearSelection}
                    selected={startIn}
                    onChange={onSelectStartDate}
                    timescale={timescale}
                    isDisabled={planPermission}
                    minDate={new Date(filterStartDate)}
                    maxDate={new Date(endIn)}
                  />
                  <Flex px={2}>
                    <BsArrowRight color={'black'} style={{ width: '20px', height: '20px' }} />
                  </Flex>
                  <DatePickerInputButton
                    clearSection={false}
                    clearDateInput={handleClearSelection}
                    selected={endIn}
                    onChange={onSelectEndDate}
                    timescale={timescale}
                    isDisabled={planPermission}
                    minDate={new Date(startIn)}
                    maxDate={new Date(filterEndDate)}
                  />
                </Flex>
                <Text color={'#8B8AAB'} fontSize={'12px'} ps={1}>
                  {timescale === 'M'
                    ? `${Number(periodAmount) + 1} months`
                    : `${Number(periodAmount) + 1} years`}
                </Text>
                {isPermissionTimescaleModal && (
                  <TimeScalePermission maxValue={maxValue} timescale={timescale} />
                )}
              </PopoverBody>
            </PopoverContent>
          </Box>
        </Popover>
      </Flex>
    </>
  );
};

export default PlanPermission(TimeRangeFilter);
